import React from "react";
import type { IconType } from "react-icons";

/**
 * Centered status card for the survey redirect outcomes (complete, terminate,
 * quota full, security terminate). Presentation only. These routes render for
 * signed-in and signed-out visitors alike, so the frame uses plain vertical
 * padding and no header offsets of its own.
 */
export type OutcomeTone = "success" | "warning" | "danger";

const toneStyles: Record<OutcomeTone, { badge: string; ring: string; label: string }> = {
  success: { badge: "bg-success-soft text-success", ring: "ring-success/10", label: "zs-badge-success" },
  warning: { badge: "bg-warning-soft text-warning", ring: "ring-warning/10", label: "zs-badge-warning" },
  danger: { badge: "bg-danger-soft text-danger", ring: "ring-danger/10", label: "zs-badge-danger" },
};

interface SurveyOutcomeCardProps {
  tone: OutcomeTone;
  icon: IconType;
  /** Short status label above the headline, e.g. "Survey complete". */
  status?: React.ReactNode;
  title: React.ReactNode;
  children?: React.ReactNode;
  actions?: React.ReactNode;
  className?: string;
}

export const SurveyOutcomeCard = ({
  tone,
  icon: Icon,
  status,
  title,
  children,
  actions,
  className = "",
}: SurveyOutcomeCardProps) => {
  const styles = toneStyles[tone];
  return (
    <div className={`flex w-full justify-center px-4 py-10 sm:py-16 lg:py-20 ${className}`}>
      <section className="zs-card w-full max-w-[480px] px-6 py-9 text-center animate-fadeInUp sm:px-10 sm:py-11">
        <span
          className={`mx-auto inline-flex h-16 w-16 items-center justify-center rounded-full ring-8 ${styles.badge} ${styles.ring}`}
        >
          <Icon className="h-7 w-7" aria-hidden="true" />
        </span>
        {status && (
          <div className="mt-6">
            <span className={`zs-badge ${styles.label}`}>{status}</span>
          </div>
        )}
        <h2 className={`${status ? "mt-3" : "mt-6"} font-display text-2xl font-bold leading-tight tracking-tight text-ink sm:text-[1.75rem]`}>
          {title}
        </h2>
        {children && <div className="mt-3 space-y-3 text-sm leading-relaxed text-ink-2 sm:text-[15px]">{children}</div>}
        {actions && <div className="mt-8 flex flex-col items-stretch gap-3">{actions}</div>}
      </section>
    </div>
  );
};
